import { bitcraftSnapshot } from './generated/bitcraftSnapshot';
import { BITJITA_ENDPOINTS } from '../services/bitjita';
import { spaceTimeDBBackendPlan } from '../services/spacetimedb';

export type LibrarySectionId = 'start-here' | 'compendium' | 'cartography' | 'market-tools' | 'contribute';

export interface DataSource {
  name: string;
  url: string;
  role: string;
  status: 'manual-fetch' | 'runtime-api' | 'planned-backend';
}

export interface LibraryEntry {
  title: string;
  description: string;
  href: string;
  badge?: string;
}

export interface LibrarySection {
  id: LibrarySectionId;
  eyebrow: string;
  title: string;
  description: string;
  entries: LibraryEntry[];
}

export interface SearchResult {
  type: 'section' | 'entry' | 'record' | 'source';
  title: string;
  description: string;
  href?: string;
}

export const dataSources: DataSource[] = [
  {
    name: 'Bitjita API',
    url: 'https://bitjita.com/docs/api',
    role: 'Primary source for items, resources, buildings, creatures, claims, market data, stats, and schema discovery.',
    status: 'runtime-api',
  },
  {
    name: 'BitCraft Map',
    url: 'https://bitcraftmap.com/',
    role: 'World context for claims, caves, temples, ruined cities, dungeons, watchtowers, grids, and resource map overlays.',
    status: 'manual-fetch',
  },
  {
    name: 'SpaceTimeDB Backend Plan',
    url: 'https://spacetimedb.com/',
    role: spaceTimeDBBackendPlan.reason,
    status: 'planned-backend',
  },
];

export const librarySections: LibrarySection[] = [
  {
    id: 'start-here',
    eyebrow: 'Start Here',
    title: 'New Wanderer Reading Room',
    description: 'A clear path for new BitCraft players before they dive into the deeper catalog.',
    entries: [
      { title: 'Getting Started', description: 'First-session route: movement, claim basics, gathering, and early crafting.', href: '#start-here', badge: 'Guide' },
      { title: 'Controls & UI', description: 'Fast lookup for navigation, map markers, tracking, and world interaction basics.', href: '#start-here', badge: 'Reference' },
      { title: 'Profession Primer', description: 'How skills, crafting stations, resources, and tier progression connect.', href: '#compendium', badge: 'Core System' },
      { title: 'Settlement Basics', description: 'Claims, citizens, buildings, markets, banks, waystones, and shared logistics.', href: '#cartography', badge: 'World' },
    ],
  },
  {
    id: 'compendium',
    eyebrow: 'Compendium',
    title: 'Stacks of Items, Resources & Buildings',
    description: 'Static TypeScript data generated from manually fetched Bitjita records, ready for richer article pages.',
    entries: [
      { title: 'Items', description: `${bitcraftSnapshot.bitjita.items.count.toLocaleString()} item records from Bitjita.`, href: 'https://bitjita.com/items', badge: 'Bitjita' },
      { title: 'Resources', description: `${bitcraftSnapshot.bitjita.resources.count.toLocaleString()} resource records plus map overlay hooks.`, href: 'https://bitjita.com/resources', badge: 'Bitjita' },
      { title: 'Buildings', description: `${bitcraftSnapshot.bitjita.buildings.count.toLocaleString()} building records for settlement planning.`, href: 'https://bitjita.com/buildings', badge: 'Bitjita' },
      { title: 'Creatures', description: `${bitcraftSnapshot.bitjita.creatures.count.toLocaleString()} creature and enemy records.`, href: 'https://bitjita.com/creatures', badge: 'Bitjita' },
      { title: 'Cargo', description: 'Cargo database integration endpoint is mapped for the next fetch pass.', href: 'https://bitjita.com/cargo', badge: 'Mapped' },
      { title: 'Crafts', description: 'Live crafting activity and recipe references belong here as the library expands.', href: 'https://bitjita.com/crafts', badge: 'Roadmap' },
    ],
  },
  {
    id: 'cartography',
    eyebrow: 'Cartographer Desk',
    title: 'World Map Intelligence',
    description: 'Claims and points of interest from BitCraft Map become article context instead of stale wiki fragments.',
    entries: [
      { title: 'Claims', description: `${bitcraftSnapshot.map.claims.count.toLocaleString()} mapped claim features.`, href: 'https://bitcraftmap.com/', badge: 'Map' },
      { title: 'Caves', description: `${bitcraftSnapshot.map.caves.count.toLocaleString()} cave features across tiers.`, href: 'https://bitcraftmap.com/', badge: 'Map' },
      { title: 'Temples', description: `${bitcraftSnapshot.map.temples.count.toLocaleString()} temple entrances indexed.`, href: 'https://bitcraftmap.com/', badge: 'Map' },
      { title: 'Ruined Cities', description: `${bitcraftSnapshot.map.ruinedCities.count.toLocaleString()} ruined city markers.`, href: 'https://bitcraftmap.com/', badge: 'Map' },
      { title: 'Dungeons', description: `${bitcraftSnapshot.map.dungeons.count.toLocaleString()} dungeon markers.`, href: 'https://bitcraftmap.com/', badge: 'Map' },
      { title: 'Watchtowers', description: `${bitcraftSnapshot.map.watchtowers.count.toLocaleString()} watchtower/grid geometries.`, href: 'https://bitcraftmap.com/', badge: 'Map' },
    ],
  },
  {
    id: 'market-tools',
    eyebrow: 'Market & Tools',
    title: 'Trade, Planning & Analysis Alcove',
    description: 'Bitjita market, claim, and calculator tools are linked now and can become first-class pages later.',
    entries: [
      { title: 'Market Browser', description: 'Browse and compare trade listings through Bitjita.', href: 'https://bitjita.com/market', badge: 'External' },
      { title: 'Deals Finder', description: 'Trade opportunity discovery for settlement logistics.', href: 'https://bitjita.com/deals', badge: 'External' },
      { title: 'Hexite Exchange', description: 'Hexite pricing and currency context.', href: 'https://bitjita.com/hexite-exchange', badge: 'External' },
      { title: 'XP Calculator', description: 'Progression estimates and skill planning.', href: 'https://bitjita.com/xp-calculator', badge: 'Tool' },
      { title: 'Settlement Planner', description: 'Layout planning that pairs well with building data.', href: 'https://bitjita.com/settlement-planner', badge: 'Tool' },
    ],
  },
  {
    id: 'contribute',
    eyebrow: 'Data Scriptorium',
    title: 'Contribution & Source Review',
    description: 'A professional contribution path for manual data checks, source snapshots, and future SpaceTimeDB-backed edits.',
    entries: [
      { title: 'Manual Fetch Workflow', description: 'Use npm run fetch:data to refresh the TypeScript snapshot from Bitjita and map exports.', href: '#data-sources', badge: 'TS' },
      { title: 'Source Confidence', description: 'Each page should show source URL, fetch time, and stale-data warnings.', href: '#data-sources', badge: 'Quality' },
      { title: 'SpaceTimeDB Escalation', description: 'Use SpaceTimeDB when edits, realtime sync, or authenticated contributors require a backend.', href: '#backend-plan', badge: 'Backend' },
    ],
  },
];

export function getCatalogStats() {
  const bitjitaRecords = Object.values(bitcraftSnapshot.bitjita).reduce((total, category) => total + category.count, 0);
  const mapRecords = Object.values(bitcraftSnapshot.map).reduce((total, category) => total + category.count, 0);

  return {
    totalRecords: bitjitaRecords + mapRecords,
    bitjitaRecords,
    mapRecords,
    sections: librarySections.length,
    sourceCount: dataSources.length,
    endpointCount: BITJITA_ENDPOINTS.length,
    fetchedAt: bitcraftSnapshot.provenance.fetchedAt,
  };
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function searchLibrary(query: string): SearchResult[] {
  const normalizedQuery = normalize(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  const haystackIncludes = (...parts: string[]) => normalize(parts.join(' ')).includes(normalizedQuery);
  const results: SearchResult[] = [];

  librarySections.forEach((section) => {
    if (haystackIncludes(section.eyebrow, section.title, section.description, section.id)) {
      results.push({ type: 'section', title: section.title, description: section.description, href: `#${section.id}` });
    }

    section.entries.forEach((entry) => {
      if (haystackIncludes(entry.title, entry.description, entry.badge ?? '')) {
        results.push({ type: 'entry', title: entry.title, description: entry.description, href: entry.href });
      }
    });
  });

  bitcraftSnapshot.records.forEach((record) => {
    if (haystackIncludes(record.name, record.kind, record.tag, record.description)) {
      results.push({
        type: 'record',
        title: `${record.name} · ${record.kind}`,
        description: `Tier ${record.tier || 'n/a'} · ${record.tag}${record.description ? ` · ${record.description}` : ''}`,
      });
    }
  });

  dataSources.forEach((source) => {
    if (haystackIncludes(source.name, source.role, source.status)) {
      results.push({ type: 'source', title: source.name, description: source.role, href: source.url });
    }
  });

  return results.slice(0, 12);
}
